import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import {
  Search, Music, Play, Pause, ShoppingCart, DollarSign,
  SlidersHorizontal, Clock, Heart, Loader2, Headphones,
  Zap, User, TrendingUp, ArrowUpDown, X, Plus
} from 'lucide-react';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest', short: 'New' },
  { value: 'popular', label: 'Most Played', short: 'Hot' },
  { value: 'price_low', label: 'Low to High', short: '$' },
  { value: 'price_high', label: 'High to Low', short: '$$$' },
  { value: 'most_liked', label: 'Most Liked', short: 'Liked' },
];

const PRICE_RANGES = [
  { label: 'All', min: null, max: null },
  { label: 'Under $30', min: null, max: 30 },
  { label: '$30-$80', min: 30, max: 80 },
  { label: '$80+', min: 80, max: null },
];

export default function BeatMarketplacePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const audioRef = useRef(null);

  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [genres, setGenres] = useState([]);

  const [activeTab, setActiveTab] = useState('browse');
  const [myPurchases, setMyPurchases] = useState([]);
  const [mySales, setMySales] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState(0);
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Audio preview
  const [playingId, setPlayingId] = useState(null);

  const LIMIT = 24;

  const fetchBeats = useCallback(async (reset = false) => {
    setLoading(true);
    const skip = reset ? 0 : page * LIMIT;
    const params = new URLSearchParams();
    params.set('limit', LIMIT);
    params.set('skip', skip);
    params.set('sort_by', sortBy);
    if (search) params.set('search', search);
    if (selectedGenre) params.set('genre', selectedGenre);
    const range = PRICE_RANGES[priceRange];
    if (range.min != null) params.set('min_price', range.min);
    if (range.max != null) params.set('max_price', range.max);

    try {
      const res = await fetch(`${API}/beat-store/marketplace?${params}`);
      if (res.ok) {
        const data = await res.json();
        setBeats(data.beats || []);
        setTotal(data.total || 0);
        if (data.genres?.length) setGenres(data.genres);
      }
    } catch (e) {
      console.error('[BeatMarketplace] Fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [search, selectedGenre, sortBy, priceRange, page]);

  useEffect(() => {
    if (activeTab === 'browse') { fetchBeats(true); setPage(0); }
  }, [search, selectedGenre, sortBy, priceRange, activeTab]);

  useEffect(() => {
    if (page > 0 && activeTab === 'browse') fetchBeats();
  }, [page]);

  useEffect(() => {
    if (activeTab === 'purchases' && isAuthenticated) fetchMyPurchases();
    if (activeTab === 'sales' && isAuthenticated) fetchMySales();
  }, [activeTab, isAuthenticated]);

  const fetchMyPurchases = async () => {
    try {
      const res = await fetch(`${API}/beat-store/my-purchases`, { headers: getAuthHeaders() });
      if (res.ok) setMyPurchases((await res.json()).purchases || []);
    } catch {}
  };

  const fetchMySales = async () => {
    try {
      const res = await fetch(`${API}/beat-store/my-sales`, { headers: getAuthHeaders() });
      if (res.ok) setMySales((await res.json()).sales || []);
    } catch {}
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBeats(true);
  };

  const togglePlay = (beat) => {
    const previewUrl = beat.preview_url;
    if (!previewUrl) return;

    if (playingId === beat.track_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    if (audioRef.current) audioRef.current.pause();
    const src = previewUrl.startsWith('http') ? previewUrl : `${API.replace('/api', '')}${previewUrl}`;
    const audio = new Audio(src);
    audio.play().catch(() => {});
    audio.onended = () => setPlayingId(null);
    audioRef.current = audio;
    setPlayingId(beat.track_id);
  };

  const hasActiveFilters = search || selectedGenre || sortBy !== 'newest' || priceRange > 0;

  return (
    <div className="min-h-screen pb-24" data-testid="beat-marketplace-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4 pt-8 pb-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-600 to-orange-700 flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-foreground" data-testid="marketplace-title">
              Beat Marketplace
            </h1>
          </div>
          <p className="text-muted-foreground text-sm ml-[52px]">
            Browse and license beats from DMV producers
          </p>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-5">
            <TabsList className="bg-muted/30">
              <TabsTrigger value="browse" data-testid="tab-browse">Browse</TabsTrigger>
              {user && <TabsTrigger value="purchases" data-testid="tab-purchases">Purchases</TabsTrigger>}
              {user && <TabsTrigger value="sales" data-testid="tab-sales">My Sales</TabsTrigger>}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pt-5">
        {/* ─── Browse Tab ─── */}
        {activeTab === 'browse' && (
          <>
            {/* Search + Filter Toggle */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-4" data-testid="marketplace-search">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search beats by name, genre..." className="pl-10 bg-card border-border h-10"
                  data-testid="marketplace-search-input" />
              </div>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-500 h-10 px-4" data-testid="marketplace-search-btn">
                <Search className="w-4 h-4" />
              </Button>
              <Button type="button" variant="outline" className="border-border h-10"
                onClick={() => setShowFilters(!showFilters)} data-testid="marketplace-filter-toggle">
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </form>

            {/* Filters Panel */}
            {showFilters && (
              <Card className="glass-card border-border p-4 mb-5" data-testid="marketplace-filters">
                <div className="space-y-4">
                  {/* Genre pills */}
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5 block">Genre</label>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => setSelectedGenre('')}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${!selectedGenre ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                        data-testid="genre-all">All</button>
                      {genres.map(g => (
                        <button key={g} onClick={() => setSelectedGenre(g === selectedGenre ? '' : g)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedGenre === g ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                          data-testid={`genre-${g}`}>{g}</button>
                      ))}
                    </div>
                  </div>
                  {/* Price Range */}
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5 block">Price</label>
                    <div className="flex flex-wrap gap-1.5">
                      {PRICE_RANGES.map((r, i) => (
                        <button key={i} onClick={() => setPriceRange(i)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${priceRange === i ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                          data-testid={`price-${i}`}>{r.label}</button>
                      ))}
                    </div>
                  </div>
                </div>
                {hasActiveFilters && (
                  <button onClick={() => { setSearch(''); setSelectedGenre(''); setSortBy('newest'); setPriceRange(0); }}
                    className="mt-3 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="clear-filters">
                    <X className="w-3 h-3" /> Clear filters
                  </button>
                )}
              </Card>
            )}

            {/* Sort Row + Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground" data-testid="results-count">
                {loading ? 'Loading...' : `${total} beat${total !== 1 ? 's' : ''} available`}
              </p>
              <div className="flex gap-1">
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setSortBy(opt.value)} data-testid={`sort-${opt.value}`}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${sortBy === opt.value ? 'bg-amber-600 text-white' : 'bg-muted/40 text-muted-foreground hover:bg-muted/60'}`}>
                    {opt.short}
                  </button>
                ))}
              </div>
            </div>

            {/* Beat Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : beats.length === 0 ? (
              <div className="text-center py-20" data-testid="no-beats">
                <Headphones className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium text-foreground">No beats found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {hasActiveFilters ? 'Try adjusting your filters' : 'No beats are currently listed for sale'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="beats-grid">
                {beats.map(beat => (
                  <BeatCard key={beat.track_id} beat={beat} playingId={playingId} onTogglePlay={togglePlay} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination">
                <Button variant="outline" size="sm" disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))} className="border-border">Prev</Button>
                <span className="text-xs text-muted-foreground">Page {page + 1} of {Math.ceil(total / LIMIT)}</span>
                <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total}
                  onClick={() => setPage(p => p + 1)} className="border-border">Next</Button>
              </div>
            )}
          </>
        )}

        {/* ─── Purchases Tab ─── */}
        {activeTab === 'purchases' && (
          <div className="space-y-3" data-testid="purchases-list">
            <h2 className="text-base font-semibold">Your Purchases</h2>
            {myPurchases.length === 0 ? (
              <Card className="p-8 bg-card border-border text-center">
                <ShoppingCart className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">No purchases yet</p>
                <Button size="sm" className="mt-3 bg-amber-600" onClick={() => setActiveTab('browse')}>Browse Beats</Button>
              </Card>
            ) : myPurchases.map(p => (
              <Card key={p.transaction_id} className="p-4 bg-card border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{p.track_title || 'Beat'}</p>
                    <p className="text-xs text-muted-foreground">{p.license_type} license</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-amber-400">${p.amount_usd?.toFixed(2)}</p>
                    <Badge className="text-[10px]" variant="outline">{p.status}</Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ─── Sales Tab ─── */}
        {activeTab === 'sales' && (
          <div className="space-y-3" data-testid="sales-list">
            <h2 className="text-base font-semibold">Your Sales</h2>
            {mySales.length === 0 ? (
              <Card className="p-8 bg-card border-border text-center">
                <DollarSign className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">No sales yet</p>
                <p className="text-xs text-muted-foreground mt-1">List a beat from your track page to start selling</p>
              </Card>
            ) : mySales.map(s => (
              <Card key={s.transaction_id} className="p-4 bg-card border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{s.track_title || 'Beat'}</p>
                    <p className="text-xs text-muted-foreground">{s.license_type} | Buyer: {s.buyer_name || 'User'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-400">${s.net_amount_usd?.toFixed(2) || s.amount_usd?.toFixed(2)}</p>
                    <Badge className="text-[10px]" variant="outline">{s.status}</Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function BeatCard({ beat, playingId, onTogglePlay }) {
  const navigate = useNavigate();
  const isPlaying = playingId === beat.track_id;

  return (
    <Card className="glass-card border-border overflow-hidden group hover:border-amber-500/30 transition-all duration-300"
      data-testid={`beat-card-${beat.track_id}`}>
      {/* Cover Art */}
      <div className="relative aspect-square bg-muted cursor-pointer" onClick={() => navigate(`/beat-store/${beat.track_id}`)}>
        {beat.cover_art ? (
          <img src={beat.cover_art} alt={beat.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-900/30 to-orange-900/30 flex items-center justify-center">
            <Music className="w-10 h-10 text-muted-foreground/30" />
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
          {beat.preview_url && (
            <button onClick={(e) => { e.stopPropagation(); onTogglePlay(beat); }}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                isPlaying ? 'bg-amber-600 scale-100 opacity-100' : 'bg-white/20 backdrop-blur-sm scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100'
              }`} data-testid={`play-${beat.track_id}`}>
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </button>
          )}
        </div>

        {/* Price badge */}
        <div className="absolute top-2 right-2">
          <Badge className="bg-emerald-600 text-white border-0 font-bold text-xs px-2 py-0.5 shadow-lg" data-testid={`price-${beat.track_id}`}>
            ${beat.price?.toFixed(2)}
          </Badge>
        </div>

        {/* BPM / Key */}
        {(beat.bpm || beat.key) && (
          <div className="absolute bottom-2 left-2 flex gap-1">
            {beat.bpm && <span className="bg-black/60 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded font-medium">{beat.bpm} BPM</span>}
            {beat.key && <span className="bg-black/60 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded font-medium">{beat.key}</span>}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5">
        <h3 className="font-semibold text-xs text-foreground truncate" data-testid={`title-${beat.track_id}`}>{beat.title}</h3>

        {/* Seller */}
        <Link to={beat.seller?.handle ? `/u/${beat.seller.handle}` : '#'}
          className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
          {beat.seller?.picture ? (
            <img src={beat.seller.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
          ) : (
            <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center">
              <User className="w-2.5 h-2.5 text-muted-foreground" />
            </div>
          )}
          <span className="text-[10px] text-muted-foreground truncate hover:text-foreground transition-colors">
            {beat.seller?.name || 'Producer'}
            {beat.seller?.is_verified && <Zap className="w-2.5 h-2.5 inline ml-0.5 text-blue-400" />}
          </span>
        </Link>

        {/* Genre + Stats */}
        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
          {beat.genre && <Badge variant="outline" className="text-[9px] border-border px-1 py-0 h-4">{beat.genre}</Badge>}
          <span className="flex items-center gap-0.5"><Play className="w-2.5 h-2.5" />{(beat.plays || 0).toLocaleString()}</span>
          <span className="flex items-center gap-0.5"><Heart className="w-2.5 h-2.5" />{beat.likes || 0}</span>
        </div>

        {/* License tiers */}
        {beat.prices && (
          <div className="flex gap-1 mt-1.5">
            <span className="text-[8px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1 py-0 rounded">
              ${beat.prices.basic}
            </span>
            <span className="text-[8px] bg-violet-500/10 text-violet-400 border border-violet-500/20 px-1 py-0 rounded">
              ${beat.prices.premium}
            </span>
            <span className="text-[8px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1 py-0 rounded">
              ${beat.prices.exclusive}
            </span>
          </div>
        )}

        {/* CTA */}
        <Button size="sm" className="w-full mt-2 bg-amber-600 hover:bg-amber-500 text-white h-7 text-[11px] font-semibold"
          onClick={() => navigate(`/beat-store/${beat.track_id}`)}
          data-testid={`buy-btn-${beat.track_id}`}>
          <ShoppingCart className="w-3 h-3 mr-1" /> View Licenses
        </Button>
      </div>
    </Card>
  );
}
